import React, { useState, useRef, useEffect } from 'react';
import { FC } from 'react';
import { capitalCase } from 'change-case';
import {
  Badge,
  Box,
  Button,
  IconButton,
  Popover,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
  MenuItem
} from '@material-ui/core';
import { Settings as SettingsIcon } from 'react-feather';
import useSettings from 'src/hooks/useSettings';
import { THEMES } from 'src/constants';
import { Theme } from 'src/theme';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) => ({
  badge: {
    height: 10,
    width: 10,
    borderRadius: 5,
    marginTop: 10,
    marginRight: 5
  },
  popover: {
    width: 320,
    padding: theme.spacing(2)
  }
}));

const langs = [
  {
    id: 'fr',
    name: 'Français'
  },
  {
    id: 'en',
    name: 'English'
  },
  {
    id: 'pt',
    name: 'Português'
  },
  {
    id: 'es',
    name: 'Español'
  },
  {
    id: 'jp',
    name: '日本人'
  }
];

const Settings: FC = () => {
  const classes = useStyles();
  const ref = useRef<any>(null);
  const { settings, saveSettings } = useSettings();
  const [isOpen, setOpen] = useState<boolean>(false);
  const { t, i18n } = useTranslation();
  const [values, setValues] = useState({
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    theme: settings.theme,
    lang: 'en'
  });

  useEffect(() => {
    setValues({
      ...values,
      lang: i18n.language
    });
  }, []);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const changeLang = (field, value): void => {
    setValues({
      ...values,
      [field]: value
    });
  };

  const changeTheme = (field, value): void => {
    setValues({
      ...values,
      [field]: value
    });
  };

  const handleSave = (): void => {
    saveSettings(values);
    i18n.changeLanguage(values.lang);
    localStorage.setItem('lng', values.lang);
    setOpen(false);
  };

  return (
    <>
      <Tooltip title="Settings">
        <Badge
          color="secondary"
          variant="standard"
          classes={{ badge: classes.badge }}
        >
          <IconButton color="inherit" onClick={handleOpen} ref={ref}>
            <SvgIcon fontSize="small">
              <SettingsIcon />
            </SvgIcon>
          </IconButton>
        </Badge>
      </Tooltip>
      <Popover
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        classes={{ paper: classes.popover }}
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
      >
        <Typography variant="h4" color="textPrimary">
          {t('Settings')}
        </Typography>
        <Box mt={2}>
          <TextField
            fullWidth
            label={t('Language')}
            name="lang"
            onChange={event => changeLang('lang', event.target.value)}
            select
            value={values.lang}
            variant="outlined"
          >
            {langs.map(option => (
              <MenuItem key={option.id} value={option.id}>
                {t(option.name)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box mt={2}>
          <TextField
            fullWidth
            label={t('Theme')}
            name="theme"
            onChange={event => changeTheme('theme', event.target.value)}
            select
            value={values.theme}
            variant="outlined"
          >
            {Object.keys(THEMES).map(theme => (
              <MenuItem key={theme} value={theme}>
                {capitalCase(theme)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box mt={2}>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={handleSave}
          >
            {t('Save')}
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default Settings;
